/**
 * ListaSalva — reabre uma lista salva (snapshot do backend). Mesmas edições do
 * Editor ao vivo (nome/qt/dimensões inline + ocultar do print + item manual),
 * agora PERSISTIDAS: é desta lista que sai a requisição de separação, então
 * ocultar aqui tira o item da requisição do almoxarife.
 * Conferência e edições são OTIMISTAS (aplicam na hora, gravam em 2º plano);
 * os campos de texto têm autosave com debounce.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiCheck, FiEye, FiEyeOff, FiMoreVertical, FiPlus, FiPrinter, FiTrash2,
} from "react-icons/fi";

import EntityHeader from "../../components/EntityHeader";
import KpiStrip from "../../components/KpiStrip";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import {
  adicionarItemLista, atualizarItemLista, conferirItem, obterLista,
  removerItemLista,
} from "../../services/revisorFerragens";
import { criarRequisicaoSeparacao } from "../../services/separacaoFerragens";
import { usePainelTerceiros } from "./PainelTerceiros";
import { enviarTerceirosDaLista } from "./enviarTerceiros";
import PainelRequisicao from "./PainelRequisicao";
import { HOLES_MODULE_ID, arredQt, groupByModule } from "./lib/parseProjeto";
import {
  ambientesSalvosParaDocumento, moduloSemCodigo, resolverModuloManual,
} from "./lib/documento";
import ManualForm, { formManualInicial } from "./ManualForm";
import { FolhasImpressao } from "./Impressao";
import {
  AmbienteCard, AmbienteFooter, AmbienteHeader, AmbienteSub, AmbienteTitle,
  ColNum, ConferCheck, DimCell, EmptyBox, ItemNota, ItemRow, ModuleCount, ModuleHeader,
  ModuleMid, ModuleName, Page, RowMenuBtn, ShowHidden, SmallInput,
} from "./styles";

const SAVED_COLS = "30px 40px 64px 1fr 120px 32px";
const DEBOUNCE_MS = 600;

function RowMenu({ item, disabled, onToggleHidden, onRemove }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "relative", justifySelf: "end" }}>
      <RowMenuBtn type="button" disabled={disabled} onClick={() => setOpen((v) => !v)} aria-label="Ações do item">
        <FiMoreVertical size={15} />
      </RowMenuBtn>
      {open ? (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 30 }} onClick={() => setOpen(false)} />
          <div
            style={{
              position: "absolute", right: 0, top: 32, zIndex: 31, minWidth: 190,
              background: "var(--surface)", border: "1px solid var(--border)",
              borderRadius: 10, boxShadow: "0 12px 28px rgba(0,0,0,.18)", padding: 4,
            }}
          >
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              style={{ width: "100%", justifyContent: "flex-start", border: "none", background: "transparent" }}
              onClick={() => { onToggleHidden(); setOpen(false); }}
            >
              {item.hidden ? <FiEye size={14} /> : <FiEyeOff size={14} />}
              {item.hidden ? "Incluir de novo" : "Ocultar da lista"}
            </button>
            {item.manual ? (
              <button
                type="button"
                className="btn btn--sm"
                style={{ width: "100%", justifyContent: "flex-start", border: "none", background: "transparent", color: "var(--danger-text)" }}
                onClick={() => { onRemove(); setOpen(false); }}
              >
                <FiTrash2 size={14} /> Remover item
              </button>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

export default function ListaSalva() {
  const { id } = useParams();
  const { request, usuario } = useAuth();
  const { pedirTerceiros, painelTerceiros } = usePainelTerceiros();
  const toast = useToast();
  const navigate = useNavigate();

  const [lista, setLista] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mostrarOcultos, setMostrarOcultos] = useState({});
  const [imprimindo, setImprimindo] = useState(false);
  const [manualAmb, setManualAmb] = useState(null); // ambiente alvo do item manual
  const [manualForm, setManualForm] = useState(null);
  const seqRef = useRef({}); // sequência por item (anti-corrida nos toggles otimistas)
  const pendenteRef = useRef({}); // itemId -> { patch, timer } (autosave dos campos de texto)
  const ultimoNomeRef = useRef({}); // itemId -> último nome não-vazio (restaura no blur)

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      setLista(await obterLista(request, id));
    } catch {
      setLista(null);
    } finally {
      setLoading(false);
    }
  }, [request, id]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const ambientes = useMemo(() => (lista ? ambientesSalvosParaDocumento(lista) : []), [lista]);

  function atualizarItemLocal(itemId, patch) {
    setLista((prev) =>
      prev
        ? { ...prev, itens: prev.itens.map((it) => (it.id === itemId ? { ...it, ...patch } : it)) }
        : prev,
    );
  }

  /** Cancela tudo que está no buffer (sem enviar). */
  const cancelarPendentes = useCallback(() => {
    Object.values(pendenteRef.current).forEach((info) => {
      if (info.timer) clearTimeout(info.timer);
    });
    pendenteRef.current = {};
  }, []);

  const enviarPatch = useCallback(async (itemId, patch) => {
    try {
      await atualizarItemLista(request, id, itemId, patch);
    } catch (err) {
      toast.error(err?.payload?.detalhe || err?.message || "Falha ao salvar a edição.");
      // Ressincroniza (edição de texto não tem revert confiável). CANCELA o
      // buffer antes: senão um patch ainda agendado gravaria DEPOIS do refetch
      // e a tela mostraria um valor e o servidor outro.
      cancelarPendentes();
      carregar();
    }
  }, [request, id, toast, carregar, cancelarPendentes]);

  /** Autosave com debounce por item — acumula as teclas num patch só. */
  function agendarPatch(itemId, patch) {
    const atual = pendenteRef.current[itemId] || { patch: {}, timer: null };
    atual.patch = { ...atual.patch, ...patch };
    if (atual.timer) clearTimeout(atual.timer);
    atual.timer = setTimeout(() => {
      const corpo = pendenteRef.current[itemId]?.patch || {};
      delete pendenteRef.current[itemId];
      if (Object.keys(corpo).length) enviarPatch(itemId, corpo);
    }, DEBOUNCE_MS);
    pendenteRef.current[itemId] = atual;
  }

  /** Descarrega o que estiver pendente (ao sair da tela / antes de imprimir). */
  const descarregarPendentes = useCallback(() => {
    Object.entries(pendenteRef.current).forEach(([itemId, info]) => {
      if (info.timer) clearTimeout(info.timer);
      if (info.patch && Object.keys(info.patch).length) enviarPatch(itemId, info.patch);
    });
    pendenteRef.current = {};
  }, [enviarPatch]);

  // Flush SÓ no unmount. Via ref de propósito: se dependesse da identidade de
  // `descarregarPendentes`, qualquer troca de identidade das deps (request/
  // toast/carregar) rodaria o cleanup a cada render e mataria o debounce
  // (viraria 1 PATCH por tecla).
  const descarregarRef = useRef(descarregarPendentes);
  descarregarRef.current = descarregarPendentes;
  useEffect(() => () => descarregarRef.current?.(), []);

  function editarCampo(item, campoLocal, campoApi, valor) {
    atualizarItemLocal(item.id, { [campoLocal]: valor });
    // Nome em branco o backend rejeita (400) — e o 400 dispara o refetch, que
    // descartaria o que o usuário está digitando. Enquanto o campo estiver
    // vazio só guardamos localmente; o onBlur restaura se ele desistir.
    if (campoApi === "nome" && !String(valor).trim()) return;
    agendarPatch(item.id, { [campoApi]: valor });
  }

  /** Campo Descrição não pode ficar vazio: ao sair, volta ao último válido. */
  function restaurarNomeSeVazio(item) {
    if (String(item.name ?? "").trim()) return;
    const salvo = (lista?.itens || []).find((i) => i.id === item.id);
    const ultimo = ultimoNomeRef.current[item.id];
    const nome = String(ultimo ?? salvo?.nome ?? "").trim();
    atualizarItemLocal(item.id, { nome: nome || "(sem nome)" });
  }

  async function alternarOculto(item) {
    const novo = !item.hidden;
    const chave = `oculto:${item.id}`;
    const seq = (seqRef.current[chave] || 0) + 1;
    seqRef.current[chave] = seq;
    atualizarItemLocal(item.id, { oculto: novo });
    try {
      await atualizarItemLista(request, id, item.id, { oculto: novo });
    } catch (err) {
      // Só reverte se nenhum toggle mais novo aconteceu neste item.
      if (seqRef.current[chave] === seq) {
        atualizarItemLocal(item.id, { oculto: !novo });
        toast.error(err?.payload?.detalhe || err?.message || "Falha ao ocultar o item.");
      }
    }
  }

  async function alternarConferencia(item) {
    const novo = !item.conferido;
    const seq = (seqRef.current[item.id] || 0) + 1;
    seqRef.current[item.id] = seq;
    // Otimista: marca na hora.
    atualizarItemLocal(item.id, {
      conferido: novo,
      conferido_por_nome: novo ? (usuario?.nome || "você") : null,
    });
    try {
      await conferirItem(request, id, item.id, novo);
    } catch (err) {
      // Só reverte se nenhum toggle mais novo aconteceu neste item (anti-corrida).
      if (seqRef.current[item.id] === seq) {
        atualizarItemLocal(item.id, { conferido: !novo });
        toast.error(err?.message || "Falha ao conferir item.");
      }
    }
  }

  function abrirManual(amb) {
    setManualAmb(amb);
    setManualForm(formManualInicial());
  }

  async function adicionarManualSalvo() {
    const amb = manualAmb;
    if (!amb || !manualForm?.nome.trim()) {
      toast.error("Informe a descrição do item.");
      return;
    }
    const mod = resolverModuloManual(manualForm, groupByModule(amb.items));
    if (!mod) {
      toast.error("Digite o nome do novo módulo.");
      return;
    }
    try {
      const item = await adicionarItemLista(request, id, {
        projeto_dinabox_id: amb.projectId,
        ambiente_nome: amb.nome,
        modulo_id: mod.moduleId === HOLES_MODULE_ID ? "" : mod.moduleId,
        modulo_nome: mod.moduleName,
        categoria: manualForm.categoria || "Outros",
        nome: manualForm.nome.trim(),
        dimensoes: manualForm.dimensoes.trim(),
        quantidade: arredQt(manualForm.quantidade) || 1,
      });
      setLista((prev) => (prev ? { ...prev, itens: [...prev.itens, item] } : prev));
      setManualAmb(null);
      setManualForm(null);
      toast.success("Item adicionado.");
    } catch (err) {
      toast.error(err?.message || "Falha ao adicionar item.");
    }
  }

  async function removerItemSalvo(item) {
    try {
      await removerItemLista(request, id, item.id);
      setLista((prev) =>
        prev ? { ...prev, itens: prev.itens.filter((it) => it.id !== item.id) } : prev,
      );
      toast.success("Item removido.");
    } catch (err) {
      toast.error(err?.message || "Falha ao remover item.");
    }
  }

  if (loading) {
    return (
      <Page>
        <EntityHeader title="Lista de compras" onBack={() => navigate("../historico")} backLabel="Voltar ao histórico" />
        <EmptyBox>Carregando…</EmptyBox>
      </Page>
    );
  }
  if (!lista) {
    return (
      <Page>
        <EntityHeader title="Lista de compras" onBack={() => navigate("../historico")} backLabel="Voltar ao histórico" />
        <EmptyBox>Lista não encontrada.</EmptyBox>
      </Page>
    );
  }

  // Mesmo fluxo do envio ao vivo: pergunta pelos terceiros ANTES de mandar ao
  // estoque. Plugar so num dos dois call sites deixaria metade dos usuarios
  // sem a pergunta.
  async function enviarAoEstoque() {
    const terceiros = await pedirTerceiros();
    if (terceiros === null) return;  // desistiu
    await criarRequisicaoSeparacao(request, lista.id);
    // Devolve p/ o FolhasImpressao avisar se algum anexo de terceiro falhou.
    return enviarTerceirosDaLista(request, lista.id, terceiros);
  }

  if (imprimindo) {
    return (
      <>
        <FolhasImpressao
          ambientes={ambientes}
          onVoltar={() => setImprimindo(false)}
          onEnviarRequisicao={enviarAoEstoque}
        />
        {painelTerceiros}
      </>
    );
  }

  const conferenciaveis = (lista.itens || []).filter((i) => !i.oculto && i.quantidade > 0);
  const conferidos = conferenciaveis.filter((i) => i.conferido).length;

  return (
    <Page>
      <EntityHeader
        title={lista.titulo}
        subtitle={`${lista.criado_por_nome ? "por " + lista.criado_por_nome + " · " : ""}${new Date(lista.created_at).toLocaleString("pt-BR")}`}
        onBack={() => navigate("../historico")}
        backLabel="Voltar ao histórico"
        primaryAction={{
          label: "Imprimir",
          icon: <FiPrinter size={14} />,
          onClick: () => { descarregarPendentes(); setImprimindo(true); },
        }}
      />

      <KpiStrip
        items={[
          { label: "Itens", value: String(conferenciaveis.length), key: "it" },
          {
            label: "Conferidos",
            value: `${conferidos} de ${conferenciaveis.length}`,
            tone: conferenciaveis.length > 0 && conferidos === conferenciaveis.length ? "success" : undefined,
            key: "cf",
          },
        ]}
      />

      <PainelRequisicao listaId={lista.id} />

      {ambientes.map((amb) => {
        const mostrar = !!mostrarOcultos[amb.projectId];
        const visiveis = amb.items.filter((it) => mostrar || !it.hidden);
        const ocultos = amb.items.filter((it) => it.hidden);
        const grupos = groupByModule(visiveis);
        let n = 0;
        return (
          <AmbienteCard key={amb.projectId}>
            <AmbienteHeader>
              <div>
                <AmbienteTitle>{amb.nome}</AmbienteTitle>
                <AmbienteSub>
                  <span>#{amb.projectId}</span>
                  <span>Cliente: {amb.cliente || "—"}</span>
                  <span>Status: {amb.status || "—"}</span>
                </AmbienteSub>
              </div>
            </AmbienteHeader>

            {grupos.map((g) => (
              <div key={g.moduleId}>
                <ModuleHeader>
                  {!moduloSemCodigo(g.moduleId) ? <ModuleMid>{g.moduleId}</ModuleMid> : null}
                  <ModuleName>{g.moduleName}</ModuleName>
                  <ModuleCount>{g.items.length} {g.items.length === 1 ? "item" : "itens"}</ModuleCount>
                </ModuleHeader>
                {g.items.map((it) => {
                  const conf = Boolean(it.conferido);
                  const numero = it.hidden ? "—" : (n += 1);
                  return (
                    <ItemRow key={it.id} $cols={SAVED_COLS} $manual={it.manual} $hidden={it.hidden} $confered={conf}>
                      <ConferCheck
                        type="button"
                        $checked={conf}
                        title={conf ? "Desmarcar conferência" : "Marcar como conferido"}
                        aria-label={conf ? "Desmarcar conferência" : "Marcar como conferido"}
                        onClick={() => alternarConferencia(it)}
                      >
                        {conf ? <FiCheck size={13} /> : null}
                      </ConferCheck>
                      <ColNum>{numero}</ColNum>
                      <SmallInput
                        $center
                        $confered={conf}
                        type="number"
                        min="0"
                        step="any"
                        disabled={conf}
                        aria-label={`Quantidade de ${it.name || "item"}`}
                        value={it.qt}
                        onChange={(e) =>
                          editarCampo(it, "quantidade", "quantidade", arredQt(e.target.value))
                        }
                      />
                      <div>
                        <SmallInput
                          $confered={conf}
                          disabled={conf}
                          aria-label="Descrição do item"
                          value={it.name}
                          onFocus={() => {
                            if (String(it.name ?? "").trim()) {
                              ultimoNomeRef.current[it.id] = it.name;
                            }
                          }}
                          onChange={(e) => editarCampo(it, "nome", "nome", e.target.value)}
                          onBlur={() => restaurarNomeSeVazio(it)}
                        />
                        {it.manufacturer || it.description ? (
                          <ItemNota title={[it.manufacturer, it.description].filter(Boolean).join(" · ")}>
                            {[it.manufacturer, it.description].filter(Boolean).join(" · ")}
                          </ItemNota>
                        ) : null}
                        {conf && it.conferidoPorNome ? (
                          <ItemNota $tamanho="10.5px" $tom="var(--success-text)" $colado>
                            conferido por {it.conferidoPorNome}
                          </ItemNota>
                        ) : null}
                      </div>
                      <DimCell>
                        <SmallInput
                          $center
                          $confered={conf}
                          disabled={conf}
                          placeholder="—"
                          aria-label="Dimensões do item"
                          value={it.dimensions}
                          onChange={(e) => editarCampo(it, "dimensoes", "dimensoes", e.target.value)}
                        />
                      </DimCell>
                      <RowMenu
                        item={it}
                        disabled={conf}
                        onToggleHidden={() => alternarOculto(it)}
                        onRemove={() => removerItemSalvo(it)}
                      />
                    </ItemRow>
                  );
                })}
              </div>
            ))}

            {!mostrar && ocultos.length > 0 ? (
              <ShowHidden onClick={() => setMostrarOcultos((m) => ({ ...m, [amb.projectId]: true }))}>
                Mostrar {ocultos.length} item(ns) ocultos
              </ShowHidden>
            ) : null}

            <AmbienteFooter>
              <span>
                {amb.items.filter((i) => !i.hidden && Number(i.qt) > 0).length} de {amb.items.length} itens
                {" "}entram na lista e na requisição
              </span>
              <button className="btn btn--secondary btn--sm" onClick={() => abrirManual(amb)}>
                <FiPlus size={13} /> Adicionar item manualmente
              </button>
            </AmbienteFooter>
          </AmbienteCard>
        );
      })}

      <SlideOverPanel
        open={!!manualAmb}
        onClose={() => { setManualAmb(null); setManualForm(null); }}
        title="Adicionar item manualmente"
        subtitle="Adiciona um item a esta lista salva."
        footer={
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn btn--secondary" onClick={() => { setManualAmb(null); setManualForm(null); }}>
              Cancelar
            </button>
            <button className="btn btn--primary" onClick={adicionarManualSalvo}>Adicionar</button>
          </div>
        }
      >
        {manualForm ? (
          <ManualForm
            form={manualForm}
            setForm={setManualForm}
            modulos={manualAmb ? groupByModule(manualAmb.items) : []}
          />
        ) : null}
      </SlideOverPanel>
    </Page>
  );
}
